import React, { useState, useEffect } from "react";
import { FaBell, FaSpinner, FaChevronLeft, FaChevronRight, FaEye, FaPaperclip } from "react-icons/fa";
import api from "../config/api";
import "./Announcements.css";

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [readAnnouncements, setReadAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSingle, setIsLoadingSingle] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 20,
    last_page: 1
  });
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch announcements on component mount
  useEffect(() => {
    fetchAnnouncements(1);
  }, []);

  const fetchAnnouncements = async (page = 1) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/announcements?page=${page}`);
      console.log('API Response:', response.data);
      
      if (response.data.success && response.data.data) {
        const apiData = response.data.data;
        
        // Extract announcements array
        const announcementsData = apiData.announcements || [];
        
        // Set unread count
        setUnreadCount(apiData.unread_count || 0);
        
        // Set pagination
        setPagination(apiData.pagination || {
          current_page: 1,
          total: 0,
          per_page: 20,
          last_page: 1
        });
        
        if (Array.isArray(announcementsData)) {
          // Sort by pinned first, then by date (newest first)
          const sortedAnnouncements = announcementsData.sort((a, b) => {
            // Pinned announcements first
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            
            // Then by creation date (newest first)
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
          
          setAnnouncements(sortedAnnouncements);
          
          // Get read announcements from localStorage
          const savedReadAnnouncements = JSON.parse(localStorage.getItem('readAnnouncements') || '[]');
          setReadAnnouncements(savedReadAnnouncements);
        } else {
          setAnnouncements([]);
          setError("Invalid announcements data format");
        }
      } else {
        setError(response.data.message || "Failed to load announcements");
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setError(error.message || "Failed to load announcements. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSingleAnnouncement = async (id) => {
    setIsLoadingSingle(true);
    try {
      const response = await api.get(`/announcements/${id}`);
      console.log('Single announcement response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.announcement) {
        return response.data.data.announcement;
      }
      return null;
    } catch (error) {
      console.error("Error fetching single announcement:", error);
      return null;
    } finally {
      setIsLoadingSingle(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      // Call API to mark as read
      const response = await api.post(`/announcements/${id}/read`);
      console.log('Mark as read response:', response.data);
      
      if (response.data.success) {
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Update local state
        const updatedReadAnnouncements = [...readAnnouncements, id];
        setReadAnnouncements(updatedReadAnnouncements);
        
        // Save to localStorage
        localStorage.setItem('readAnnouncements', JSON.stringify(updatedReadAnnouncements));
        
        // Update announcement in list
        setAnnouncements(prev => prev.map(item => 
          item.id === id 
            ? { 
                ...item, 
                is_read: true, 
                read_at: new Date().toISOString() 
              }
            : item
        ));
        
        // Update selected announcement if it's the same
        if (selectedAnnouncement && selectedAnnouncement.id === id) {
          setSelectedAnnouncement(prev => ({
            ...prev,
            is_read: true,
            read_at: new Date().toISOString()
          }));
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error marking announcement as read:", error);
      return false;
    }
  };

  const handleOpen = async (announcement) => {
    try {
      // First try to get fresh data from API
      const freshAnnouncement = await fetchSingleAnnouncement(announcement.id);
      
      if (freshAnnouncement) {
        setSelectedAnnouncement(freshAnnouncement);
      } else {
        // Fallback to the announcement from list
        setSelectedAnnouncement(announcement);
      }
      
      // Mark as read if not already read
      if (!announcement.is_read && !readAnnouncements.includes(announcement.id)) {
        await markAsRead(announcement.id);
      }
    } catch (error) {
      console.error("Error opening announcement:", error);
      setSelectedAnnouncement(announcement);
      
      // Still try to mark as read
      if (!announcement.is_read && !readAnnouncements.includes(announcement.id)) {
        await markAsRead(announcement.id);
      }
    }
  };

  const handleClose = () => {
    setSelectedAnnouncement(null);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchAnnouncements(page);
    }
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return "";
      }
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return "Recent";
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return "Recent";
      }
      
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 1) {
        return "Just now";
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      } else if (diffDays === 1) {
        return "Yesterday";
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (error) {
      return "Recent";
    }
  };

  // Function to get priority badge color
  const getPriorityColor = (priority) => {
    if (!priority) return '#6B7280';
    
    switch (priority.toLowerCase()) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  // Function to get scope badge color
  const getScopeColor = (scope) => {
    if (!scope) return '#6B7280';
    
    switch (scope.toLowerCase()) {
      case 'nationwide':
        return '#3B82F6';
      case 'state':
        return '#8B5CF6';
      case 'lga':
        return '#10B981';
      case 'ward':
        return '#F59E0B';
      case 'polling_unit':
        return '#EC4899';
      default:
        return '#6B7280';
    }
  };

  // Format scope text
  const formatScope = (scope) => {
    if (!scope) return "";
    
    switch (scope.toLowerCase()) {
      case 'nationwide':
        return 'Nationwide';
      case 'polling_unit':
        return 'Polling Unit';
      default:
        return scope.toUpperCase();
    }
  };

  // Render attachment preview
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;
    
    return (
      <div className="attachments-list">
        {attachments.map((attachment, index) => (
          <div key={index} className="attachment-item">
            <FaPaperclip className="attachment-icon" />
            <span className="attachment-name">
              {attachment.filename || `Attachment ${index + 1}`}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="announcements-page">
      {/* Header */}
      <div className="announcements-header">
        <div className="header-left">
          <FaBell className="bell-icon" />
          <h2>Announcements</h2>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
        </div>
        {!isLoading && !error && announcements.length > 0 && (
          <span className="announcement-count">
            {pagination.total} announcement{pagination.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <FaSpinner className="spinner" />
          <p>Loading announcements...</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => fetchAnnouncements(1)} className="retry-button">
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && announcements.length === 0 && (
        <div className="empty-container">
          <div className="empty-icon">📢</div>
          <p>No announcements available at the moment.</p>
          <p className="empty-subtext">Check back later for updates!</p>
        </div>
      )}

      {/* Announcements List */}
      {!isLoading && !error && announcements.length > 0 && (
        <>
          <div className="announcement-container">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className={`announcement-card ${
                  (readAnnouncements.includes(announcement.id) || announcement.is_read) 
                    ? "read" 
                    : "unread"
                } ${announcement.is_pinned ? "pinned" : ""}`}
                onClick={() => handleOpen(announcement)}
              >
                {announcement.is_pinned && (
                  <div className="pinned-badge">📌 Pinned</div>
                )}
                
                <div className="announcement-header">
                  <h4>
                    {announcement.title}
                    {!(readAnnouncements.includes(announcement.id) || announcement.is_read) && (
                      <span className="dot">•</span>
                    )}
                  </h4>
                  <div className="badges-container">
                    {announcement.priority && (
                      <span 
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(announcement.priority) }}
                      >
                        {announcement.priority.charAt(0).toUpperCase()}
                      </span>
                    )}
                    {announcement.scope && (
                      <span 
                        className="scope-badge"
                        style={{ backgroundColor: getScopeColor(announcement.scope) }}
                      >
                        {formatScope(announcement.scope)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="announcement-meta">
                  <p className="date">
                    {formatRelativeDate(announcement.created_at)}
                  </p>
                  {announcement.admin && (
                    <p className="admin-info">
                      By: {announcement.admin.name}
                    </p>
                  )}
                </div>
                
                <p className="message-preview">
                  {announcement.content 
                    ? (announcement.content.length > 120 
                      ? `${announcement.content.substring(0, 120)}...` 
                      : announcement.content)
                    : "No content available"}
                </p>
                
                {announcement.attachments && (
                  <div className="attachments-info">
                    <FaPaperclip className="attachment-icon" />
                    <span className="attachment-text">Has attachments</span>
                  </div>
                )}
                
                <div className="announcement-footer">
                  {(readAnnouncements.includes(announcement.id) || announcement.is_read) ? (
                    <span className="read-status done">
                      ✓ Read
                    </span>
                  ) : (
                    <span className="read-status">
                      <FaEye /> View
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="pagination-container">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
              >
                <FaChevronLeft /> Previous
              </button>
              
              <div className="page-info">
                Page {pagination.current_page} of {pagination.last_page}
              </div>
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
              >
                Next <FaChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal for detailed view */}
      {selectedAnnouncement && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Loading */}
            {isLoadingSingle && (
              <div className="modal-loading">
                <FaSpinner className="spinner" />
                <p>Loading announcement details...</p>
              </div>
            )}
            
            {!isLoadingSingle && (
              <>
                <div className="modal-header">
                  <h3>{selectedAnnouncement.title}</h3>
                  <div className="modal-badges">
                    {selectedAnnouncement.priority && (
                      <span 
                        className="priority-badge modal-priority"
                        style={{ backgroundColor: getPriorityColor(selectedAnnouncement.priority) }}
                      >
                        {selectedAnnouncement.priority.charAt(0).toUpperCase() + selectedAnnouncement.priority.slice(1)} Priority
                      </span>
                    )}
                    {selectedAnnouncement.scope && (
                      <span 
                        className="scope-badge modal-scope"
                        style={{ backgroundColor: getScopeColor(selectedAnnouncement.scope) }}
                      >
                        {formatScope(selectedAnnouncement.scope)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="modal-info">
                  <div className="modal-meta">
                    <p className="date">
                      <strong>Sent:</strong> {formatDate(selectedAnnouncement.created_at)}
                    </p>
                    {selectedAnnouncement.expires_at && (
                      <p className="expiry-date">
                        <strong>Expires:</strong> {formatDate(selectedAnnouncement.expires_at)}
                      </p>
                    )}
                    {selectedAnnouncement.admin && (
                      <p className="admin-info">
                        <strong>From:</strong> {selectedAnnouncement.admin.name}
                      </p>
                    )}
                  </div>
                  
                  <div className="modal-status">
                    {selectedAnnouncement.is_pinned && (
                      <span className="pinned-indicator">📌 Pinned</span>
                    )}
                    {(readAnnouncements.includes(selectedAnnouncement.id) || selectedAnnouncement.is_read) && (
                      <span className="read-indicator">✓ Read</span>
                    )}
                  </div>
                </div>
                
                <div className="modal-body">
                  <div className="message-content">
                    {selectedAnnouncement.content}
                  </div>
                  
                  {selectedAnnouncement.attachments && selectedAnnouncement.attachments.length > 0 && (
                    <div className="modal-attachments">
                      <h4>
                        <FaPaperclip /> Attachments ({selectedAnnouncement.attachments.length})
                      </h4>
                      {renderAttachments(selectedAnnouncement.attachments)}
                    </div>
                  )}
                  
                  {selectedAnnouncement.read_at && (
                    <div className="read-info">
                      <strong>Read at:</strong> {formatDate(selectedAnnouncement.read_at)}
                    </div>
                  )}
                </div>
                
                <div className="modal-footer">
                  <button className="close-btn" onClick={handleClose}>
                    Close
                  </button>
                  
                  {!selectedAnnouncement.is_read && !readAnnouncements.includes(selectedAnnouncement.id) && (
                    <button 
                      className="mark-read-btn"
                      onClick={() => markAsRead(selectedAnnouncement.id)}
                    >
                      Mark as Read
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;